import { To, type NavigateFunction } from 'react-router-dom';

import { surveysApi } from 'services/surveys/surveys';
import { useLazyGetArticleByIdQuery } from 'services/wm/wm';

import { RootState, store } from 'store';

import { notifyToast } from 'shared/Toast/Toast';
import { logDataDogEvent } from 'utils/datadog';
import { PathName } from 'router/paths';
import { buildSelectPrescriptionsTarget } from 'router/renewalTargets';

import { BaseAppointmentProps } from 'models/appointment.types';
import { Program, UserProgram } from 'models/programs.types';
import { ActionType, Survey } from 'models/survey.types';

export const handleCreateAppt = (query: URLSearchParams) => {
  query.delete('action');
  return {
    pathname: PathName.CreateAppointment,
    search: query.toString()
  };
};

type GetAppointmentLazyTrigger = (id: string) => {
  unwrap: () => Promise<{ data: BaseAppointmentProps }>;
};

export const handleDeepLinkAppointmentReschedule = async (
  appointmentId: string,
  getAppointment: GetAppointmentLazyTrigger
): Promise<To | null> => {
  try {
    const { data: appointment } = await getAppointment(appointmentId).unwrap();
    if (appointment.isReschedulable) {
      return {
        pathname: `${PathName.Appointment}/${appointmentId}`,
        search: 'reschedule=true'
      };
    }
    return { pathname: `${PathName.Appointment}/${appointmentId}` };
  } catch {
    return null;
  }
};

export const handleDeepLinkNoShowFeeCharged = async (
  appointmentId: string,
  getAppointment: GetAppointmentLazyTrigger,
  navigate: NavigateFunction
): Promise<To | null> => {
  try {
    const { data: appointment } = await getAppointment(appointmentId).unwrap();
    if (appointment?.noShowCharge?.status === 'charged') {
      const feeDollars = (appointment.noShowCharge.feeAmountCents / 100).toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
      notifyToast(
        `We missed you today. A $${feeDollars} no-show fee was applied.`,
        { autoClose: false, toastId: 'no-show-fee-charged' },
        () => {
          navigate({ hash: 'billing-history-section', pathname: PathName.BillingDetails });
        },
        'See details'
      );
    }
    return { pathname: PathName.Home };
  } catch {
    return null;
  }
};

const MEDICATION_SURVEY_TYPES: ActionType[] = [
  ActionType.Renewal,
  ActionType.MonthlyMedicationCheckIn,
  ActionType.WeeklyMedicationCheckIn,
  ActionType.CheckIn
];

// Device types first, then the medication family - `useIncompleteSurvey`'s order, which this list otherwise mirrors.
// `WeeklyMedicationCheckIn` trails the rest because mobile marks it non-important, so it only wins when nothing else is open.
// `MoodCheckIn` is deliberately absent: `GET /surveys` carries it, but web answers it inline on the Mental Health page and has no route to deep-link to.
const INCOMPLETE_SURVEY_TYPE_PRIORITY: ActionType[] = [
  ActionType.FirstDeviceCheckIn,
  ActionType.WeeklyDeviceCheckIn,
  ActionType.MonthlyDeviceCheckIn,
  ActionType.MonthlyMedicationCheckIn,
  ActionType.Renewal,
  ActionType.CheckIn,
  ActionType.WeeklyMedicationCheckIn
];

export const isMedicationSurveyType = (type: ActionType): boolean =>
  MEDICATION_SURVEY_TYPES.includes(type);

export const resolveFirstIncompleteSurveyOfOrder = (
  surveys: Survey[],
  orderId: string
): Survey | undefined => surveys.find((s) => s.orderId === orderId && !s.completedAt);

const pickByTypePriority = (surveys: Survey[]): Survey | undefined =>
  INCOMPLETE_SURVEY_TYPE_PRIORITY.map((type) => surveys.filter((s) => s.type === type).at(-1)).find(
    Boolean
  );

/**
 * Picks the survey a param-less deep link opens, mirroring mobile's `firstNavigableSurvey`:
 *   surveys on a program the patient currently has active are preferred, and the rest of the list is a fallback rather than a filter.
 *
 * @param surveys - Everything `GET /surveys` returned; completed surveys are dropped here.
 * @param activeProgramIds - Ids of the patient's active programs, used only to rank. Omit to rank on survey type alone.
 */
export const resolveFirstIncompleteSurvey = (
  surveys: Survey[],
  activeProgramIds?: Set<string>
): Survey | undefined => {
  const incompleteSurveys = surveys.filter((s) => !s.completedAt);
  // `programId` is derived server-side by `defineSurveyUserProgram` and omitted when it cannot be resolved, so a survey may carry none at all.
  const activeProgramSurveys = activeProgramIds?.size
    ? incompleteSurveys.filter((s) => !!s.programId && activeProgramIds.has(s.programId))
    : [];
  return pickByTypePriority(activeProgramSurveys) ?? pickByTypePriority(incompleteSurveys);
};

const buildSurveyIdTarget = (surveyId: string, step?: string | null): To => ({
  pathname: PathName.Survey,
  search: `surveyId=${surveyId}${step ? `&step=${step}` : ''}`
});

const buildDeviceStatusTarget = (orderId: string, type: string, step?: string | null): To => ({
  pathname: PathName.DeviceStatus,
  search: `orderId=${orderId}&type=${type}${step ? `&step=${step}` : ''}`
});

// Renewals enter through the prescription-selection screen, which is what
// `/surveys/:surveyId/prescriptions` describes; the other medication types have no selection
// step. A `step` link (e.g. `step=updateCard`) targets the survey page itself, so it skips the
// selection screen — that page owns the step.
const buildMedicationSurveyTarget = (survey: Survey, step?: string | null): To =>
  survey.type === ActionType.Renewal && !step
    ? buildSelectPrescriptionsTarget(survey._id)
    : buildSurveyIdTarget(survey._id, step);

// Dispatches a resolved survey to its destination page - used where the survey's own
// type is the only signal available (no query `type` to branch on ahead of time).
const buildSurveyTarget = (survey: Survey, step?: string | null): To =>
  isMedicationSurveyType(survey.type)
    ? buildMedicationSurveyTarget(survey, step)
    : buildDeviceStatusTarget(survey.orderId, survey.type, step);

export interface DeepLinkSurveyResolution {
  /**
   * The survey the destination was resolved from, so the caller can gate eligibility on its own
   * type instead of the link's. `null` for the device family, which deliberately keeps routing
   * (and its orderId-keyed eligibility check) on the query params.
   */
  survey: Survey | null;
  target: To;
}

export const handleDeepLinkSurvey = async (
  query: URLSearchParams,
  fetchSurveys?: () => Promise<Survey[]>,
  activeUserPrograms?: UserProgram[]
): Promise<DeepLinkSurveyResolution | null> => {
  const surveyId = query.get('surveyId');
  const orderId = query.get('orderId');
  const step = query.get('step');
  const type = (query.get('type') || 'renewal') as ActionType;

  let surveys = surveysApi.endpoints.getSurveys.select()(store.getState() as RootState)?.data ?? [];
  let refetched = false;
  // Mirrors mobile's `fetchFirstIncompleteSurveyBy`: read what is already loaded, then force one
  // fetch before giving up. A deep link opened on a cold start (app launch straight into the
  // link, dashboard never mounted) has no cache to read.
  const refetchSurveys = async () => {
    if (refetched || !fetchSurveys) return;
    refetched = true;
    try {
      surveys = await fetchSurveys();
    } catch {
      // Keep whatever the cache held.
    }
  };

  if (!surveys.length) await refetchSurveys();

  // 1. Unambiguous link - the resolved survey decides the destination, by family (renewals open
  // the selection screen, device check-ins their status page). The link's own `type` is not
  // trusted for that: it is absent on most surveyId links and defaults to `renewal` here.
  if (surveyId) {
    let linkedSurvey = surveys.find((s) => s._id === surveyId);
    if (!linkedSurvey) {
      await refetchSurveys();
      linkedSurvey = surveys.find((s) => s._id === surveyId);
    }
    // `GET /surveys` lists only open surveys, so a miss means completed, canceled or deleted —
    // nothing to open, the same early return mobile's navigateToSurvey does.
    if (!linkedSurvey || linkedSurvey.completedAt) return null;
    return { survey: linkedSurvey, target: buildSurveyTarget(linkedSurvey, step) };
  }

  // 2. Legacy grace-period link - resolve best-effort from the surveys list.
  if (orderId) {
    if (isMedicationSurveyType(type)) {
      const resolvedSurvey = resolveFirstIncompleteSurveyOfOrder(surveys, orderId);
      return resolvedSurvey
        ? { survey: resolvedSurvey, target: buildMedicationSurveyTarget(resolvedSurvey, step) }
        : null;
    }

    // Device family - preserve today's exact behavior: confirm existence, keep routing on
    // the query params (not the resolved survey), no completedAt filtering.
    const survey = surveys.find((s) => s.orderId === orderId);
    return survey ? { survey: null, target: buildDeviceStatusTarget(orderId, type, step) } : null;
  }

  // 3. Param-less link - resolve to the patient's first incomplete survey overall.
  const activeProgramIds = new Set((activeUserPrograms ?? []).map((up) => up.program._id));
  const survey = resolveFirstIncompleteSurvey(surveys, activeProgramIds);
  return survey ? { survey, target: buildSurveyTarget(survey) } : null;
};

export const resolveProgramByQuery = <T extends Program | UserProgram>(
  programFromQuery: string,
  programs?: T[]
): { program: T; type: 'id' | 'code' | 'category' } | null => {
  if (!programFromQuery || !programs) return null;
  const isUserProgram = (p: Program | UserProgram): p is UserProgram => 'program' in p;
  let returnedProgram: {
    program: T;
    type: 'id' | 'code' | 'category';
  } | null = null;
  programs.forEach((p) => {
    const id = isUserProgram(p) ? p.program._id : p._id;
    const code = isUserProgram(p) ? p.program.code : p.code;
    const category = isUserProgram(p) ? p.program.category : p.category;
    if (id === programFromQuery) {
      returnedProgram = {
        program: p,
        type: 'id'
      };
    }
    if (code === programFromQuery) {
      returnedProgram = {
        program: p,
        type: 'code'
      };
    }
    if (category === programFromQuery) {
      returnedProgram = {
        program: p,
        type: 'category'
      };
    }
  });
  return returnedProgram;
};

/**
 * Collects paused program IDs that are eligible to resume from the provided programs.
 *
 * @param userPrograms - List of user programs to inspect.
 * @returns Program IDs for paused subscriptions that have a scheduled resume date.
 */
export const getPausedProgramIds = (userPrograms?: UserProgram[]): string[] => {
  if (!userPrograms?.length) return [];

  return userPrograms
    .filter((program) => !!program.subscription?.subscriptionOnHold)
    .map((program) => program.program._id);
};
/**
 * Resolves the deep link destination for article pages ensuring the user only lands on
 * resources tied to a program available to them (or an explicit fallback).
 */
export const handleDeepLinkResourcesPage = async (
  getArticleById: ReturnType<typeof useLazyGetArticleByIdQuery>[0],
  articleId: string | null,
  programFromQuery: string,
  userPrograms?: UserProgram[]
): Promise<To | null> => {
  if (!articleId) return null;

  try {
    const response = await getArticleById({ articleId }).unwrap();
    const articleProgramIds = response.data?.programIds ?? [];
    const matchedProgramId = userPrograms?.find((p) => articleProgramIds.includes(p.program._id))
      ?.program._id;

    if (!matchedProgramId) {
      return {
        pathname: PathName.Home
      };
    }

    return {
      pathname: `${PathName.MyResources}/${matchedProgramId}/article/${articleId}`
    };
  } catch (error) {
    const programData = resolveProgramByQuery(programFromQuery, userPrograms);
    // Log API failure for debugging; fallback to programId if available
    logDataDogEvent({
      error: new Error(error instanceof Error ? error.message : 'DeepLink error'),
      level: 'warn',
      message: 'handleDeepLinkResourcesPage - getArticleById failed'
    });
    if (!programData) {
      return {
        pathname: PathName.Home
      };
    }

    return {
      pathname: `${PathName.MyResources}/${programData.program.program._id}/article/${articleId}`
    };
  }
};
